// Display · NumberValue
//
// Locale-formatted numeric value at a fixed precision (tabular numerals,
// em-dash on null). Composes TextValue; formatting via `useFormat().number`.
// Used as the generator's default display for numeric/integer columns.

import { TextValue } from "./TextValue";
import { useFormat } from "@ds/display/useNumberFormat";
import { withExportSpec } from "@ds/display/exportSpec";

export interface NumberValueInput {
  value: number | null | undefined;
  /** Fraction digits. Default 2 (integers pass 0). */
  precision?: number;
  testId?: string;
}

export type NumberValueProps = NumberValueInput;

export function NumberValue({ value, precision = 2, testId }: NumberValueProps) {
  const { number } = useFormat();
  return (
    <TextValue
      value={value == null ? undefined : number(value, precision)}
      tabular
      testId={testId}
    />
  );
}

// ─── Excel export ─────────────────────────────────────────────────────────
//
// Exports as a native number at the same precision the cell renders, so the
// column stays summable without the pl-PL thousands separators travelling with
// it as text.

withExportSpec<typeof NumberValue, NumberValueProps>(NumberValue, {
  type: "number",
  value: (p) => p.value,
  numFmt: (p) => {
    const digits = p.precision ?? 2;
    return digits > 0 ? `#,##0.${"0".repeat(digits)}` : "#,##0";
  },
});
